import { useState, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../lib/api'

export interface Source {
  document: string
  page: number
  chunk: string
}

export interface WebSource {
  url: string
  title: string
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  webSources?: WebSource[]
  timestamp: Date
}

export function useChat() {
  const { getToken } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)

  const sendMessage = useCallback(
    async (content: string, mentionedDocIds: string[] = [], mentionedContacts: Array<{ name: string; email: string }> = []) => {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)

      try {
        const token = await getToken()
        const response = await api.chat(
          {
            message: content,
            mentionedDocuments: mentionedDocIds,
            mentionedContacts: mentionedContacts.length > 0 ? mentionedContacts : undefined,
            sessionId,
            history: messages.map(m => ({ role: m.role, content: m.content })),
          },
          token
        )

        if (response.sessionId && !sessionId) {
          setSessionId(response.sessionId)
        }

        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response.message,
          sources: response.sources,
          webSources: response.web_sources,
          timestamp: new Date(),
        }

        setMessages((prev) => [...prev, assistantMessage])
      } catch (error) {
        console.error('Chat error:', error)
        const errorMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Sorry, an error occurred. Please try again.',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsLoading(false)
      }
    },
    [getToken, sessionId, messages]
  )

  const clearChat = useCallback(() => {
    setMessages([])
    setSessionId(null)
  }, [])

  return {
    messages,
    sendMessage,
    isLoading,
    sessionId,
    clearChat,
  }
}
